import React, { useState } from 'react';
import { ExclamationCircleOutlined, CloseOutlined, LoadingOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { deployService } from '../services/deploy.service';
import { useNotificationContext } from '../contexts/NotificationContext';
import { formatBytes, formatDateHumanReadable } from '../utils/formatters';

interface OldestRelease {
  deployId: string;
  createdAt: string;
  sizeBytes: number;
}

interface DeployReleaseDeleteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDeleteSuccess: () => void;
  currentReleases?: number;
  maxReleases?: number;
  oldestRelease?: OldestRelease;
}

/**
 * Modal shown when MAX_RELEASES_REACHED error occurs
 * Allows user to delete the oldest release to make room for a new deploy
 */
const DeployReleaseDeleteModal: React.FC<DeployReleaseDeleteModalProps> = ({
  isOpen,
  onClose,
  onDeleteSuccess,
  currentReleases = 0,
  maxReleases = 0,
  oldestRelease,
}) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const { success, error: showError } = useNotificationContext();

  if (!isOpen) return null;

  const handleDeleteRelease = async () => {
    if (!oldestRelease?.deployId) {
      showError('No release information available');
      return;
    }

    setIsDeleting(true);
    try {
      const response = await deployService.deleteRelease(oldestRelease.deployId);
      success(`Release deleted. Freed ${formatBytes(response.freedBytes)}`);
      onDeleteSuccess();
      onClose();
    } catch (err: any) {
      console.error('Error deleting release:', err);
      showError(
        err.message || 'Failed to delete release. Please try again.'
      );
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-50 animate-fade-in"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="bg-white dark:bg-dark-bg rounded-xl shadow-2xl max-w-md w-full animate-scale-in"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-start justify-between p-6 pb-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 text-orange-600 dark:text-orange-400">
                <ExclamationCircleOutlined className="text-2xl" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                  Storage Limit Reached
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                  Delete oldest release to continue deploying
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              disabled={isDeleting}
              className="p-2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors disabled:opacity-50"
            >
              <CloseOutlined />
            </button>
          </div>

          {/* Content */}
          <div className="px-6 pb-4 space-y-4">
            {/* Release count */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                Current releases: <span className="font-bold">{currentReleases} of {maxReleases}</span>
              </p>
            </div>

            {/* Oldest release details */}
            {oldestRelease && (
              <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-3">
                <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                  Oldest Release
                </p>

                <div className="space-y-2">
                  <div>
                    <p className="text-xs text-gray-600 dark:text-gray-400">Deployment ID</p>
                    <p className="text-sm font-mono text-gray-900 dark:text-white break-all">
                      {oldestRelease.deployId}
                    </p>
                  </div>

                  <div>
                    <p className="text-xs text-gray-600 dark:text-gray-400">Created</p>
                    <p className="text-sm text-gray-900 dark:text-white">
                      {formatDateHumanReadable(oldestRelease.createdAt)}
                    </p>
                  </div>

                  <div>
                    <p className="text-xs text-gray-600 dark:text-gray-400">Size</p>
                    <p className="text-sm text-gray-900 dark:text-white font-semibold">
                      {formatBytes(oldestRelease.sizeBytes)}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Warning message */}
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
              <p className="text-sm text-yellow-800 dark:text-yellow-200">
                Deleting this release will free up space and allow your deployment to proceed.
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 bg-gray-50 dark:bg-gray-800 rounded-b-xl">
            <button
              onClick={onClose}
              disabled={isDeleting}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleDeleteRelease}
              disabled={isDeleting}
              className={`
                px-4 py-2 text-sm font-medium rounded-lg transition-colors
                disabled:opacity-50 disabled:cursor-not-allowed
                bg-red-600 hover:bg-red-700 text-white
              `}
            >
              {isDeleting ? (
                <div className="flex items-center gap-2">
                  <LoadingOutlined className="animate-spin" />
                  <span>Deleting...</span>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <span>Delete & Continue Deploy</span>
                </div>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default DeployReleaseDeleteModal;
